import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AffordabilityBanner, QuoteNegotiationActions } from '../components/QuoteAffordability';
import {
  Container, Paper, Typography, Box, Grid, Card, CardContent,
  Button, Alert, CircularProgress, Chip, Tooltip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Slider, Collapse, Divider, useMediaQuery, MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ArrowBack, CompareArrows, EmojiEvents, Warning, CheckCircle,
  Close, Help, TrendingDown, TrendingUp, Schedule, AttachMoney,
  Shield, ExpandMore, ExpandLess, Print, Assessment, Star
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartsTooltip,
  Cell, LabelList
} from 'recharts';
import {
  calculatePriceStats, isAnomalyBid, getAnomalyDeviation,
  getAllCostCategories, getLowestForCategory, getBidCategoryAmount,
  buildScopeMatrix, getScopeStatus, calculateScores, getTopScoredBid,
  formatCurrency, hasMixedCurrencies, parseCostBreakdown,
  parseWarrantyMonths, ensureArray, DEFAULT_WEIGHTS, WEIGHT_LABELS,
  durationInDays, formatDuration
} from '../utils/bidComparisonUtils';
import { brand, status as statusColors, text, bg, border, shadow, chart } from '../theme/colors';

// ── Inline Sub-Components ────────────────────────────────────────────────

const SectionTitle = ({ children, icon, action }) => (
  <Box sx={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    mb: 2.5, pb: 1.5, borderBottom: `2px solid ${brand.primary}20`
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {icon && <Box sx={{ color: brand.primary }}>{icon}</Box>}
      <Typography variant="h6" sx={{ fontWeight: 700, color: text.primary, letterSpacing: '-0.01em' }}>
        {children}
      </Typography>
    </Box>
    {action}
  </Box>
);

const MetricChip = ({ label, value, best, color }) => (
  <Box sx={{
    display: 'inline-flex', alignItems: 'center', gap: 0.5,
    px: 1.5, py: 0.5, borderRadius: 2,
    bgcolor: best ? `${statusColors.success}12` : bg.subtle,
    border: best ? `1px solid ${statusColors.success}40` : `1px solid ${border.light}`
  }}>
    <Typography variant="caption" sx={{ color: text.secondary }}>{label}:</Typography>
    <Typography variant="caption" sx={{ fontWeight: 700, color: color || text.primary }}>
      {value}
    </Typography>
    {best && <EmojiEvents sx={{ fontSize: 12, color: '#fbbf24' }} />}
  </Box>
);

// ── Main Component ───────────────────────────────────────────────────────

const BidComparisonPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { getApiClient, user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [bids, setBids] = useState([]);
  const [projectName, setProjectName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [weights, setWeights] = useState(DEFAULT_WEIGHTS);
  const [scoringOpen, setScoringOpen] = useState(false);
  const [mobileCompareIds, setMobileCompareIds] = useState([]);

  useEffect(() => {
    loadBids();
  }, [projectId]);

  const loadBids = async () => {
    try {
      setLoading(true);
      setError('');
      const apiClient = getApiClient();
      const response = await apiClient.get(`/bids/project/${projectId}`);

      if (response.data.success) {
        // Filter to only submitted/under_review bids for comparison
        const comparableBids = (response.data.data || []).filter(
          b => ['submitted', 'under_review'].includes(b.status)
        );
        setBids(comparableBids);

        if (comparableBids.length > 0) {
          setProjectName(comparableBids[0].project_name || 'Project');
          // Default mobile selection: first 2 bids
          setMobileCompareIds(comparableBids.slice(0, 2).map(b => b.id));
        }
      }
    } catch (err) {
      console.error('Error loading bids:', err);
      setError('Failed to load bids for comparison');
    } finally {
      setLoading(false);
    }
  };

  // Determine which bids to show (mobile = 2, desktop = all)
  const visibleBids = useMemo(() => {
    if (!isMobile) return bids;
    return bids.filter(b => mobileCompareIds.includes(b.id));
  }, [bids, isMobile, mobileCompareIds]);

  // Computed analytics
  const priceStats = useMemo(() => calculatePriceStats(bids), [bids]);
  const scores = useMemo(() => calculateScores(bids, weights), [bids, weights]);
  const topBidId = useMemo(() => getTopScoredBid(scores), [scores]);
  const costCategories = useMemo(() => getAllCostCategories(bids), [bids]);
  const scopeMatrix = useMemo(() => buildScopeMatrix(bids), [bids]);
  const mixedCurrencies = useMemo(() => hasMixedCurrencies(bids), [bids]);

  const lowestPriceBid = useMemo(() => {
    if (bids.length === 0) return null;
    return bids.reduce((low, b) => parseFloat(b.total_cost || Infinity) < parseFloat(low.total_cost || Infinity) ? b : low, bids[0]);
  }, [bids]);

  const fastestBid = useMemo(() => {
    if (bids.length === 0) return null;
    return bids.reduce((fast, b) => {
      const bDays = durationInDays(b) || Infinity;
      const fastDays = durationInDays(fast) || Infinity;
      return bDays < fastDays ? b : fast;
    }, bids[0]);
  }, [bids]);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress size={48} sx={{ color: brand.primary }} />
        <Typography variant="body1" sx={{ mt: 2, color: text.secondary }}>Loading bid comparison...</Typography>
      </Container>
    );
  }

  if (bids.length < 2) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          At least 2 submitted bids are needed for comparison. Currently {bids.length} bid(s) available.
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/bids/review')}>
          Back to Bid Review
        </Button>
      </Container>
    );
  }

  const bidColors = chart.palette;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {user?.role === 'client' && <AffordabilityBanner sx={{ mb: 2 }} />}
      {/* ── Header ──────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }} className="no-print">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/bids/review')} sx={{ color: text.secondary }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: text.primary, letterSpacing: '-0.03em' }}>
              Bid Comparison
            </Typography>
            <Typography variant="body2" sx={{ color: text.secondary }}>
              {projectName} — {bids.length} bids received
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={() => window.print()}
            size="small"
          >
            Print
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
      {mixedCurrencies && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<Warning />}>
          Bids use different currencies. Price comparisons may not be directly meaningful.
        </Alert>
      )}

      {/* ── Mobile Bid Selector ─────────────────────────────────────── */}
      {isMobile && bids.length > 2 && (
        <Paper elevation={0} sx={{ p: 2, mb: 3, border: `1px solid ${border.light}`, borderRadius: 2 }} className="no-print">
          <Typography variant="caption" sx={{ color: text.secondary, fontWeight: 600, mb: 1, display: 'block' }}>
            Select 2 bids to compare:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {[0, 1].map(idx => (
              <FormControl key={idx} size="small" sx={{ flex: 1 }}>
                <Select
                  value={mobileCompareIds[idx] || ''}
                  onChange={(e) => {
                    const newIds = [...mobileCompareIds];
                    newIds[idx] = e.target.value;
                    setMobileCompareIds(newIds);
                  }}
                >
                  {bids.map(b => (
                    <MenuItem key={b.id} value={b.id} disabled={mobileCompareIds.includes(b.id) && mobileCompareIds[idx] !== b.id}>
                      {b.contractor_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))}
          </Box>
        </Paper>
      )}

      {/* ── Quick Stats Bar ─────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5, mb: 3, borderRadius: 3,
          background: brand.gradient, color: '#fff',
          display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center',
          position: 'relative', overflow: 'hidden'
        }}
      >
        <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
        <Box>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>Price Range</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {formatCurrency(priceStats.min)} — {formatCurrency(priceStats.max)}
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
        <Box>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>Average</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatCurrency(priceStats.average)}</Typography>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
        <Box>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>Spread</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{priceStats.spreadPercent.toFixed(0)}%</Typography>
        </Box>
        <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
        <Box>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>Quotations</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{bids.length}</Typography>
        </Box>
      </Paper>

      {/* ── 1. Summary Scorecards ───────────────────────────────────── */}
      <Box sx={{ mb: 4 }}>
        <SectionTitle icon={<CompareArrows />}>Bidder Overview</SectionTitle>
        <Grid container spacing={2.5}>
          {visibleBids.map((bid, idx) => {
            const isLowest = lowestPriceBid?.id === bid.id;
            const isFastest = fastestBid?.id === bid.id;
            const isTop = topBidId == bid.id;
            const bidScore = scores[bid.id];
            const anomaly = isAnomalyBid(parseFloat(bid.total_cost || 0), priceStats.average);
            const anomalyDev = getAnomalyDeviation(parseFloat(bid.total_cost || 0), priceStats.average);
            const bidColor = bidColors[idx % bidColors.length];

            return (
              <Grid item xs={12} sm={6} md={Math.min(4, 12 / visibleBids.length)} key={bid.id}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    border: isTop ? `2px solid ${brand.primary}` : `1px solid ${border.light}`,
                    position: 'relative',
                    transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.25s',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: shadow.lg }
                  }}
                >
                  {isTop && (
                    <Box sx={{
                      position: 'absolute', top: -1, left: 0, right: 0,
                      height: 4, background: brand.gradient, borderRadius: '12px 12px 0 0'
                    }} />
                  )}
                  <CardContent sx={{ p: 3 }}>
                    {/* Contractor name + badges */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Box sx={{
                        width: 10, height: 10, borderRadius: '50%',
                        bgcolor: bidColor, flexShrink: 0
                      }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, color: text.primary, flex: 1, letterSpacing: '-0.01em' }}>
                        {bid.contractor_name}
                      </Typography>
                      {isTop && (
                        <Tooltip title="Highest composite score">
                          <Chip icon={<EmojiEvents sx={{ fontSize: 14 }} />} label="Top Rated" size="small"
                            sx={{ bgcolor: `${brand.accent}15`, color: brand.accent, fontWeight: 700, fontSize: '0.65rem' }} />
                        </Tooltip>
                      )}
                    </Box>

                    {/* Total cost — hero number */}
                    <Typography variant="h4" sx={{
                      fontWeight: 800, letterSpacing: '-0.03em', mb: 0.5,
                      color: isLowest ? statusColors.success : text.primary
                    }}>
                      {formatCurrency(bid.total_cost, bid.currency)}
                    </Typography>

                    {/* Anomaly warning */}
                    {anomaly && (
                      <Alert severity="warning" icon={<Warning sx={{ fontSize: 16 }} />} sx={{ py: 0, px: 1, mb: 1.5, '& .MuiAlert-message': { fontSize: '0.7rem' } }}>
                        {anomalyDev.toFixed(0)}% below average — review scope carefully
                      </Alert>
                    )}

                    {/* Metric chips */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 2 }}>
                      <MetricChip label="Duration" value={formatDuration(bid)} best={isFastest} />
                      <MetricChip label="Deposit" value={formatCurrency(bid.deposit_amount, bid.currency)} />
                      <MetricChip label="Warranty" value={bid.warranty_period || 'N/A'} />
                      <MetricChip label="Inclusions" value={ensureArray(bid.inclusions).length} />
                    </Box>

                    {user?.role === 'client' && (
                      <QuoteNegotiationActions
                        kind="bid" id={bid.id}
                        total={bid.total_cost} currency={bid.currency || 'USD'}
                        sx={{ mb: 2 }}
                      />
                    )}

                    {/* Composite score bar */}
                    {bidScore && (
                      <Box sx={{ mt: 'auto' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" sx={{ color: text.secondary, fontWeight: 500 }}>Composite Score</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: brand.primary }}>
                            {bidScore.composite.toFixed(0)}/100
                          </Typography>
                        </Box>
                        <Box sx={{
                          height: 6, borderRadius: 3, bgcolor: `${brand.primary}12`,
                          overflow: 'hidden'
                        }}>
                          <Box sx={{
                            height: '100%', borderRadius: 3,
                            width: `${bidScore.composite}%`,
                            background: brand.gradient,
                            transition: 'width 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)'
                          }} />
                        </Box>
                      </Box>
                    )}

                    {/* Actions */}
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate(`/bids/${bid.id}`)}
                        sx={{ flex: 1, fontWeight: 600 }}
                      >
                        Full Details
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* ── 2. Price Chart ──────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ p: 3, mb: 4, border: `1px solid ${border.light}`, borderRadius: 3 }}>
        <SectionTitle icon={<AttachMoney />}>Price Comparison</SectionTitle>
        <Box sx={{ height: Math.max(200, visibleBids.length * 60 + 40) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={visibleBids.map((bid, idx) => ({
                name: bid.contractor_name?.split(' ').slice(0, 2).join(' '),
                total: parseFloat(bid.total_cost || 0),
                color: bidColors[bids.indexOf(bid) % bidColors.length],
                isLowest: lowestPriceBid?.id === bid.id
              }))}
              layout="vertical"
              margin={{ top: 5, right: 80, left: 10, bottom: 5 }}
            >
              <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 11, fill: text.secondary }} />
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12, fill: text.primary, fontWeight: 600 }} />
              <RechartsTooltip
                formatter={(value) => [formatCurrency(value), 'Total Bid']}
                contentStyle={{ borderRadius: 8, border: `1px solid ${border.light}`, boxShadow: shadow.sm }}
              />
              <Bar dataKey="total" radius={[0, 6, 6, 0]} barSize={28}>
                {visibleBids.map((bid, idx) => (
                  <Cell
                    key={bid.id}
                    fill={lowestPriceBid?.id === bid.id ? statusColors.success : bidColors[bids.indexOf(bid) % bidColors.length]}
                  />
                ))}
                <LabelList dataKey="total" position="right" formatter={(v) => formatCurrency(v)}
                  style={{ fontSize: 11, fontWeight: 600, fill: text.primary }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      {/* ── 3. Cost Breakdown Table ─────────────────────────────────── */}
      {costCategories.length > 0 && (
        <Paper elevation={0} sx={{ p: 3, mb: 4, border: `1px solid ${border.light}`, borderRadius: 3 }}>
          <SectionTitle icon={<Assessment />}>Cost Breakdown Comparison</SectionTitle>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: text.primary, bgcolor: bg.subtle, minWidth: 160 }}>
                    Category
                  </TableCell>
                  {visibleBids.map((bid, idx) => (
                    <TableCell key={bid.id} align="right" sx={{
                      fontWeight: 700, color: text.primary, bgcolor: bg.subtle,
                      borderBottom: `3px solid ${bidColors[bids.indexOf(bid) % bidColors.length]}`
                    }}>
                      {bid.contractor_name?.split(' ')[0]}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {costCategories.map(category => {
                  const lowest = getLowestForCategory(bids, category);
                  return (
                    <TableRow key={category} hover>
                      <TableCell sx={{ fontWeight: 500, color: text.primary }}>{category}</TableCell>
                      {visibleBids.map(bid => {
                        const amount = getBidCategoryAmount(bid, category);
                        const isLow = amount !== null && amount === lowest && amount > 0;
                        return (
                          <TableCell key={bid.id} align="right" sx={{
                            fontWeight: isLow ? 700 : 400,
                            color: isLow ? statusColors.success : amount === null ? text.disabled : text.primary,
                            bgcolor: isLow ? statusColors.successBg : 'transparent'
                          }}>
                            {amount !== null ? formatCurrency(amount, bid.currency) : '—'}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
                {/* Total row */}
                <TableRow sx={{ bgcolor: bg.subtle }}>
                  <TableCell sx={{ fontWeight: 700, color: text.primary, borderTop: `2px solid ${border.default}` }}>
                    TOTAL
                  </TableCell>
                  {visibleBids.map(bid => {
                    const isLow = lowestPriceBid?.id === bid.id;
                    return (
                      <TableCell key={bid.id} align="right" sx={{
                        fontWeight: 700, borderTop: `2px solid ${border.default}`,
                        color: isLow ? statusColors.success : text.primary,
                        bgcolor: isLow ? statusColors.successBg : bg.subtle
                      }}>
                        {formatCurrency(bid.total_cost, bid.currency)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── 4. Scope Coverage Matrix ────────────────────────────────── */}
      {scopeMatrix.items.length > 0 && (
        <Paper elevation={0} sx={{ p: 3, mb: 4, border: `1px solid ${border.light}`, borderRadius: 3 }}>
          <SectionTitle icon={<Shield />}>
            Scope Coverage Matrix
          </SectionTitle>
          {scopeMatrix.riskItems.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }} icon={<Warning />}>
              {scopeMatrix.riskItems.length} scope item{scopeMatrix.riskItems.length > 1 ? 's' : ''} flagged —
              one bidder includes it while another excludes it. Review highlighted rows carefully.
            </Alert>
          )}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: text.primary, bgcolor: bg.subtle, minWidth: 200 }}>
                    Scope Item
                  </TableCell>
                  {visibleBids.map((bid, idx) => (
                    <TableCell key={bid.id} align="center" sx={{
                      fontWeight: 700, color: text.primary, bgcolor: bg.subtle,
                      borderBottom: `3px solid ${bidColors[bids.indexOf(bid) % bidColors.length]}`
                    }}>
                      {bid.contractor_name?.split(' ')[0]}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {scopeMatrix.items.map(item => {
                  const isRisk = scopeMatrix.riskItems.includes(item);
                  return (
                    <TableRow key={item} sx={{
                      bgcolor: isRisk ? statusColors.warningBg : 'transparent',
                      '&:hover': { bgcolor: isRisk ? statusColors.warningBg : bg.hover }
                    }}>
                      <TableCell sx={{
                        fontWeight: isRisk ? 600 : 400, color: text.primary,
                        textTransform: 'capitalize',
                        display: 'flex', alignItems: 'center', gap: 0.5
                      }}>
                        {isRisk && <Warning sx={{ fontSize: 14, color: statusColors.warning }} />}
                        {item}
                      </TableCell>
                      {visibleBids.map(bid => {
                        const status = getScopeStatus(scopeMatrix.bidMaps, bid.id, item);
                        const cellConfig = {
                          included: { icon: <CheckCircle sx={{ fontSize: 18, color: statusColors.success }} />, bg: statusColors.successBg },
                          excluded: { icon: <Close sx={{ fontSize: 18, color: statusColors.error }} />, bg: statusColors.errorBg },
                          not_mentioned: { icon: <Help sx={{ fontSize: 16, color: text.disabled }} />, bg: 'transparent' },
                        }[status];

                        return (
                          <TableCell key={bid.id} align="center" sx={{ bgcolor: cellConfig.bg }}>
                            <Tooltip title={status === 'included' ? 'Included' : status === 'excluded' ? 'Excluded' : 'Not mentioned'}>
                              {cellConfig.icon}
                            </Tooltip>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── 5. Timeline Comparison ──────────────────────────────────── */}
      <Paper elevation={0} sx={{ p: 3, mb: 4, border: `1px solid ${border.light}`, borderRadius: 3 }}>
        <SectionTitle icon={<Schedule />}>Timeline Comparison</SectionTitle>
        <Grid container spacing={2}>
          {visibleBids.map((bid, idx) => {
            const maxDuration = Math.max(...bids.map(b => durationInDays(b)));
            const duration = durationInDays(bid);
            const barWidth = maxDuration > 0 ? (duration / maxDuration) * 100 : 0;
            const isFast = fastestBid?.id === bid.id;
            const bidColor = bidColors[bids.indexOf(bid) % bidColors.length];

            return (
              <Grid item xs={12} key={bid.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" sx={{
                    fontWeight: 600, color: text.primary, minWidth: 120,
                    display: 'flex', alignItems: 'center', gap: 0.5
                  }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: bidColor, flexShrink: 0 }} />
                    {bid.contractor_name?.split(' ').slice(0, 2).join(' ')}
                  </Typography>
                  <Box sx={{ flex: 1, position: 'relative' }}>
                    <Box sx={{
                      height: 24, borderRadius: 2,
                      width: `${barWidth}%`, minWidth: 40,
                      bgcolor: isFast ? statusColors.success : bidColor,
                      display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                      px: 1, transition: 'width 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)'
                    }}>
                      <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700, fontSize: '0.7rem' }}>
                        {formatDuration(bid)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ minWidth: 100, textAlign: 'right' }}>
                    <Typography variant="caption" sx={{ color: text.secondary }}>
                      {bid.estimated_start_date ? new Date(bid.estimated_start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                      {' → '}
                      {bid.estimated_completion_date ? new Date(bid.estimated_completion_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                    </Typography>
                  </Box>
                  {isFast && <Chip label="Fastest" size="small" sx={{ bgcolor: statusColors.successBg, color: statusColors.success, fontWeight: 600, fontSize: '0.65rem' }} />}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* ── 6. Weighted Scoring ─────────────────────────────────────── */}
      <Paper elevation={0} sx={{ p: 3, mb: 4, border: `1px solid ${border.light}`, borderRadius: 3 }} className="no-print">
        <Box
          sx={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          onClick={() => setScoringOpen(!scoringOpen)}
        >
          <SectionTitle icon={<Star />}>
            Weighted Scoring
          </SectionTitle>
          {scoringOpen ? <ExpandLess sx={{ color: text.secondary }} /> : <ExpandMore sx={{ color: text.secondary }} />}
        </Box>
        <Collapse in={scoringOpen}>
          <Typography variant="body2" sx={{ color: text.secondary, mb: 3 }}>
            Adjust the importance of each criterion. Scores are automatically calculated — lower price, shorter timeline, and lower deposit score higher. More inclusions and longer warranty score higher.
          </Typography>

          {/* Weight sliders */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {Object.entries(weights).map(([key, value]) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <Box sx={{ px: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: text.primary }}>
                      {WEIGHT_LABELS[key]}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: brand.primary }}>
                      {value}%
                    </Typography>
                  </Box>
                  <Slider
                    value={value}
                    onChange={(_, newVal) => setWeights(prev => ({ ...prev, [key]: newVal }))}
                    min={0}
                    max={100}
                    step={5}
                    size="small"
                    sx={{
                      color: brand.primary,
                      '& .MuiSlider-thumb': { width: 14, height: 14 }
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Score comparison table */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, bgcolor: bg.subtle }}>Criterion</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: bg.subtle }} align="center">Weight</TableCell>
                  {visibleBids.map((bid, idx) => (
                    <TableCell key={bid.id} align="center" sx={{
                      fontWeight: 700, bgcolor: bg.subtle,
                      borderBottom: `3px solid ${bidColors[bids.indexOf(bid) % bidColors.length]}`
                    }}>
                      {bid.contractor_name?.split(' ')[0]}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(WEIGHT_LABELS).map(([key, label]) => (
                  <TableRow key={key} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{label}</TableCell>
                    <TableCell align="center" sx={{ color: text.secondary }}>{weights[key]}%</TableCell>
                    {visibleBids.map(bid => {
                      const score = scores[bid.id]?.[key] || 0;
                      const isHigh = score >= 80;
                      const isLow = score <= 30;
                      return (
                        <TableCell key={bid.id} align="center" sx={{
                          fontWeight: 600,
                          color: isHigh ? statusColors.success : isLow ? statusColors.error : text.primary,
                          bgcolor: isHigh ? statusColors.successBg : isLow ? statusColors.errorBg : 'transparent'
                        }}>
                          {score.toFixed(0)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
                {/* Composite row */}
                <TableRow sx={{ bgcolor: bg.subtle }}>
                  <TableCell sx={{ fontWeight: 700, borderTop: `2px solid ${border.default}` }}>
                    COMPOSITE SCORE
                  </TableCell>
                  <TableCell align="center" sx={{ borderTop: `2px solid ${border.default}` }}>—</TableCell>
                  {visibleBids.map(bid => {
                    const composite = scores[bid.id]?.composite || 0;
                    const isTop = topBidId == bid.id;
                    return (
                      <TableCell key={bid.id} align="center" sx={{
                        fontWeight: 700, fontSize: '1.1rem',
                        borderTop: `2px solid ${border.default}`,
                        color: isTop ? brand.primary : text.primary,
                        bgcolor: isTop ? `${brand.primary}08` : bg.subtle
                      }}>
                        {composite.toFixed(0)}
                        {isTop && <EmojiEvents sx={{ fontSize: 14, ml: 0.5, color: '#fbbf24', verticalAlign: 'middle' }} />}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      </Paper>

      {/* ── 7. Decision Actions ─────────────────────────────────────── */}
      <Paper elevation={0} sx={{ p: 3, border: `1px solid ${border.light}`, borderRadius: 3 }} className="no-print">
        <SectionTitle icon={<CheckCircle />}>Make a Decision</SectionTitle>
        <Grid container spacing={2}>
          {visibleBids.map((bid, idx) => {
            const isTop = topBidId == bid.id;
            const bidColor = bidColors[bids.indexOf(bid) % bidColors.length];
            return (
              <Grid item xs={12} sm={6} md={Math.min(4, 12 / visibleBids.length)} key={bid.id}>
                <Card elevation={0} sx={{
                  p: 2, border: `1px solid ${isTop ? brand.primary : border.light}`,
                  borderRadius: 2, textAlign: 'center'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: bidColor }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {bid.contractor_name}
                    </Typography>
                    {isTop && <Chip label="Recommended" size="small" sx={{ bgcolor: `${brand.primary}12`, color: brand.primary, fontWeight: 700, fontSize: '0.6rem' }} />}
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: text.primary, mb: 2 }}>
                    {formatCurrency(bid.total_cost, bid.currency)}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      onClick={() => navigate(`/bids/${bid.id}`)}
                      sx={{
                        background: brand.gradient, fontWeight: 600,
                        '&:hover': { background: brand.gradientHover }
                      }}
                    >
                      Review & Approve
                    </Button>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    </Container>
  );
};

export default BidComparisonPage;
